import React, { useEffect, useMemo, useState } from "react";

interface SidebarSpotlightOverlayProps {
    active: boolean;
    targetSelector: string;
    padding?: number;
    zIndex?: number;
}

type RectState = {
    top: number;
    left: number;
    width: number;
    height: number;
};

const SidebarSpotlightOverlay: React.FC<SidebarSpotlightOverlayProps> = ({
    active,
    targetSelector,
    padding = 8,
    zIndex = 1200,
}) => {
    const [rect, setRect] = useState<RectState | null>(null);

    useEffect(() => {
        if (!active) {
            setRect(null);
            return;
        }

        let rafId: number | null = null;
        let intervalId: number | null = null;

        const shouldUpdateRect = (next: RectState | null, prev: RectState | null): boolean => {
            if (!next && !prev) return false;
            if (!next || !prev) return true;
            const EPS = 0.5;
            return (
                Math.abs(next.top - prev.top) > EPS ||
                Math.abs(next.left - prev.left) > EPS ||
                Math.abs(next.width - prev.width) > EPS ||
                Math.abs(next.height - prev.height) > EPS
            );
        };

        const updateRect = () => {
            const target = document.querySelector(targetSelector);
            if (!target) {
                setRect((prev) => (prev != null ? null : prev));
                return;
            }

            const bounds = target.getBoundingClientRect();
            if (bounds.width <= 0 || bounds.height <= 0) {
                setRect((prev) => (prev != null ? null : prev));
                return;
            }

            const safePadding = Math.max(0, Number.isFinite(padding) ? padding : 0);
            const nextRect: RectState = {
                top: Math.max(0, bounds.top - safePadding),
                left: Math.max(0, bounds.left - safePadding),
                width: Math.max(0, bounds.width + safePadding * 2),
                height: Math.max(0, bounds.height + safePadding * 2),
            };

            setRect((prev) => (shouldUpdateRect(nextRect, prev) ? nextRect : prev));
        };

        const scheduleUpdate = () => {
            if (rafId != null) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updateRect);
        };

        scheduleUpdate();
        // Keep re-checking while active so the overlay can lock on once the target mounts.
        intervalId = window.setInterval(scheduleUpdate, 200);
        window.addEventListener("resize", scheduleUpdate);
        window.addEventListener("scroll", scheduleUpdate, true);

        return () => {
            if (rafId != null) cancelAnimationFrame(rafId);
            if (intervalId != null) window.clearInterval(intervalId);
            window.removeEventListener("resize", scheduleUpdate);
            window.removeEventListener("scroll", scheduleUpdate, true);
        };
    }, [active, targetSelector, padding]);

    const pieces = useMemo(() => {
        if (!rect || !active) return null;

        const top = Math.max(0, rect.top);
        const left = Math.max(0, rect.left);
        const right = Math.min(window.innerWidth, left + rect.width);
        const bottom = Math.min(window.innerHeight, top + rect.height);

        return [
            { key: "top", top: 0, left: 0, width: window.innerWidth, height: top },
            { key: "left", top, left: 0, width: left, height: Math.max(0, bottom - top) },
            { key: "right", top, left: right, width: Math.max(0, window.innerWidth - right), height: Math.max(0, bottom - top) },
            { key: "bottom", top: bottom, left: 0, width: window.innerWidth, height: Math.max(0, window.innerHeight - bottom) },
        ];
    }, [active, rect]);

    if (!active || !rect || !pieces) return null;

    return (
        <div
            aria-hidden
            style={{
                position: "fixed",
                inset: 0,
                zIndex,
                pointerEvents: "none",
            }}
        >
            {pieces.map((piece) =>
                piece.width > 0 && piece.height > 0 ? (
                    <div
                        key={piece.key}
                        style={{
                            position: "fixed",
                            top: `${piece.top}px`,
                            left: `${piece.left}px`,
                            width: `${piece.width}px`,
                            height: `${piece.height}px`,
                            backgroundColor: "rgba(2, 6, 23, 0.28)",
                            backdropFilter: "blur(1px)",
                            pointerEvents: "none",
                        }}
                    />
                ) : null,
            )}
            <div
                style={{
                    position: "fixed",
                    top: `${rect.top}px`,
                    left: `${rect.left}px`,
                    width: `${rect.width}px`,
                    height: `${rect.height}px`,
                    borderRadius: "12px",
                    boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.28), 0 0 0 10px rgba(59, 130, 246, 0.08)",
                    pointerEvents: "none",
                }}
            />
        </div>
    );
};

export default SidebarSpotlightOverlay;
